import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { HelpCircle } from "lucide-react";
import { useI18n } from "../lib/i18n";
import { resolvePageHelp } from "../lib/page-help";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";

function HelpSections({ sections }) {
  return (
    <div className="space-y-5 text-sm">
      {sections.map((section) => (
        <section key={section.title}>
          <h3 className="font-semibold text-[var(--text-1)] mb-2">{section.title}</h3>
          {section.body && <p className="text-[var(--text-2)] leading-relaxed">{section.body}</p>}
          {section.steps && (
            <ol className="list-decimal list-inside space-y-1.5 text-[var(--text-2)] leading-relaxed">
              {section.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          )}
          {section.items && (
            <ul className="list-disc list-inside space-y-1.5 text-[var(--text-2)] leading-relaxed">
              {section.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          )}
        </section>
      ))}
    </div>
  );
}

export default function PageHelp({ pathname, variant = "header" }) {
  const location = useLocation();
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const help = resolvePageHelp(pathname || location.pathname, lang);
  if (!help) return null;

  const testId = `page-help-${(pathname || location.pathname || "/").replace(/\//g, "-").replace(/^-/, "") || "home"}`;
  const isHeader = variant === "header";
  const isCompact = variant === "compact";

  return (
    <>
      {isHeader ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 px-3 h-11 rounded-2xl bg-[var(--surface)] border border-[var(--border-c)] text-xs font-semibold text-[var(--text-2)] hover:text-[var(--text-1)] shadow-sm"
          data-testid={testId}
        >
          <HelpCircle size={14} /> {t("help")}
        </button>
      ) : isCompact ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="flex items-center gap-1.5 px-3 h-9 rounded-xl bg-[var(--surface-2)] text-xs font-semibold text-[var(--text-2)] hover:text-[var(--text-1)]"
          data-testid={testId}
        >
          <HelpCircle size={13} /> {t("help")}
        </button>
      ) : (
        <Button type="button" variant="outline" onClick={() => setOpen(true)} data-testid={testId}>
          <HelpCircle size={14} className="mr-2" /> {t("help")}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid={`${testId}-dialog`}>
          <DialogHeader>
            <DialogTitle>{help.title}</DialogTitle>
            {help.description && <DialogDescription>{help.description}</DialogDescription>}
          </DialogHeader>
          <HelpSections sections={help.sections || []} />
          <DialogFooter>
            <Button type="button" onClick={() => setOpen(false)}>{t("help_got_it")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
